import { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import Marquee from "react-fast-marquee";
import { 
  Phone, 
  MapPin, 
  Clock, 
  Star, 
  Menu as MenuIcon, 
  X, 
  ChevronDown,
  Facebook,
  ExternalLink,
  Utensils,
  Flame
} from "lucide-react";
import { Button } from "../components/ui/button";

// Restaurant Data
const restaurantData = {
  name: "Al Giardino",
  tagline: "L'autentica pizzeria rustica a Monfalcone",
  rating: 4.1,
  reviewCount: 1129,
  priceRange: "10-20 €",
  address: "Via Iº Maggio, 112, 34074 Monfalcone GO",
  phone: "0481 410669",
  hours: "Pranzo: 12:00 - 15:00 • Cena: 18:00 - 23:30 • Chiuso il Lunedì",
  services: ["Consumazione sul posto", "Squisite pizze napoletane"],
  facebook: "m.facebook.com"
};

const menuCategories = [
  {
    name: "Antipasti",
    items: [
      { name: "Prosciutto Crudo e Bufala", description: "Prosciutto crudo di Parma e mozzarella di bufala", price: "13,00" },
      { name: "Prosciutto Crudo, Burrata e Pomodorini", description: "Prosciutto crudo, burrata cremosa e pomodorini freschi", price: "14,00" },
      { name: "Carpaccio di Filetto di Manzo", description: "Carpaccio di manzo con rucola e grana", price: "18,00" },
      { name: "Antipasto Misto Mare", description: "Selezione di antipasti di pesce", price: "18,00" },
      { name: "Tagliere di Affettati", description: "Selezione di salumi tipici", price: "13,00" },
    ]
  },
  {
    name: "Primi Piatti",
    items: [
      { name: "Spaghetti alle Vongole", description: "Spaghetti con vongole fresche", price: "14,00" },
      { name: "Risotto alla Pescatora", description: "Risotto con frutti di mare (min. 2 persone)", price: "15,00" },
      { name: "Scialatielli alla Scogliera", description: "Pasta fresca con frutti di mare misti", price: "18,00" },
      { name: "Linguine ai Crostacei", description: "Linguine con gamberi e scampi", price: "19,00" },
      { name: "Spaghetti alla Carbonara", description: "Spaghetti con uovo, pecorino e pancetta", price: "11,00" },
      { name: "Gnocchi alla Sorrentina", description: "Gnocchi con pomodoro e mozzarella filante", price: "10,50" },
    ]
  },
  {
    name: "Pizze Classiche",
    items: [
      { name: "Margherita", description: "Pomodoro, mozzarella, olio", price: "7,00" },
      { name: "Marinara", description: "Pomodoro, aglio, origano, olio", price: "6,00" },
      { name: "Diavola", description: "Pomodoro, mozzarella, salamino piccante", price: "8,50" },
      { name: "Quattro Stagioni", description: "Pomodoro, mozzarella, carciofi, prosciutto, funghi, acciughe", price: "9,00" },
      { name: "Capricciosa", description: "Pomodoro, mozzarella, carciofi, funghi, prosciutto", price: "9,00" },
      { name: "Quattro Formaggi", description: "Pomodoro, mozzarella, gorgonzola, emmental, grana", price: "10,00" },
    ]
  },
  {
    name: "Pizze Speciali",
    items: [
      { name: "Pizza Al Giardino", description: "Pomodoro, mozzarella, peperoni, melanzane, spinaci, zucchine, grana", price: "11,00" },
      { name: "Pizza Frutti di Mare", description: "Pomodoro, frutti di mare, prezzemolo, aglio", price: "12,00" },
      { name: "Valtellina", description: "Pomodoro, mozzarella, rucola, bresaola, grana a scaglie", price: "11,00" },
      { name: "Mari e Monti", description: "Pomodoro, mozzarella, funghi di bosco, frutti di mare", price: "13,00" },
      { name: "8 Gusti", description: "Pomodoro, mozzarella, carciofi, funghi, prosciutto, peperoni, wurstel, olive, cipolla, acciughe", price: "11,00" },
    ]
  },
  {
    name: "Calzoni",
    items: [
      { name: "Calzone Napoli", description: "Mozzarella, prosciutto, ricotta, salamino, pepe, grana", price: "12,00" },
      { name: "Calzone Farcito", description: "Pomodoro, mozzarella, prosciutto, funghi, carciofi", price: "9,00" },
      { name: "Calzone Amalfi", description: "Mozzarella, prosciutto, ricotta, spinaci, grana", price: "9,00" },
      { name: "Calzone alla Romana", description: "Pomodoro, mozzarella, prosciutto, ricotta", price: "8,50" },
    ]
  },
  {
    name: "Secondi di Pesce",
    items: [
      { name: "Calamari alla Griglia", description: "Calamari freschi alla griglia", price: "15,50" },
      { name: "Frittura Mista", description: "Calamari, gamberi e pesce fritto", price: "18,00" },
      { name: "Grigliata Mista di Pesce", description: "Selezione di pesce alla griglia", price: "20,00" },
      { name: "Gamberoni ai Ferri", description: "Gamberoni freschi alla griglia", price: "18,00" },
      { name: "Branzino Acqua Pazza", description: "Branzino in salsa acqua pazza", price: "15,00" },
    ]
  },
  {
    name: "Secondi di Carne",
    items: [
      { name: "Tagliata di Manzo", description: "Tagliata di manzo con rucola e grana", price: "20,00" },
      { name: "Roast Beef", description: "Roast beef servito con contorno", price: "16,00" },
      { name: "Cotoletta alla Milanese", description: "Cotoletta impanata con patatine fritte", price: "10,00" },
      { name: "Grigliata di Carne", description: "Selezione di carni alla griglia", price: "16,00" },
    ]
  },
  {
    name: "Dolci",
    items: [
      { name: "Tiramisù", description: "Il classico dolce italiano", price: "5,00" },
      { name: "Profiteroles", description: "Bignè con crema e cioccolato", price: "5,00" },
      { name: "Torta Caprese", description: "Torta al cioccolato e mandorle", price: "6,00" },
      { name: "Tartufo Affogato al Caffè", description: "Tartufo al cioccolato con caffè espresso", price: "6,50" },
      { name: "Saint Honoré", description: "Dolce francese con crema e caramello", price: "5,00" },
    ]
  }
];

const reviews = [
  {
    author: "David Zanutta",
    badge: "Local Guide · 91 recensioni",
    date: "1 mese fa",
    text: "Locale tranquillo un po vecchio stile, la pizza è ottima e nel menù c'è una vasta scelta per ogni gusto. Attesa un po' lunga ma se non si è di fretta non è un problema.",
    rating: 4
  },
  {
    author: "Chiara Liguori",
    badge: "Local Guide · 38 recensioni",
    date: "2 anni fa",
    text: "Ci siamo fermati a cena di ritorno a Trieste dall'aeroporto, ottima posizione sulla statale con ampio parcheggio. Il locale era pienissimo, sabato sera credo sia consigliato prenotare!",
    rating: 5
  },
  {
    author: "Marco B.",
    badge: "67 recensioni",
    date: "3 mesi fa",
    text: "Pizza buonissima in un locale storico di Monfalcone, super consigliata!!! Prezzi nella norma e servizio cordiale.",
    rating: 5
  },
  {
    author: "Laura P.",
    badge: "12 recensioni",
    date: "6 mesi fa",
    text: "Molto gradito anche il piatto con roast beef, patatine e formaggio alla piastra. Ambiente familiare e accogliente.",
    rating: 4
  },
  {
    author: "Giovanni R.",
    badge: "45 recensioni",
    date: "2 mesi fa",
    text: "Pizze e secondi squisiti e prezzi nella norma. Un classico di Monfalcone che non delude mai.",
    rating: 4
  }
];

const images = {
  hero: "https://images.pexels.com/photos/29626982/pexels-photo-29626982.jpeg",
  exterior: "https://customer-assets.emergentagent.com/job_monfalcone-pizzeria/artifacts/slqhqo0x_Screenshot%202026-03-23%20221849.png",
  interior: "https://customer-assets.emergentagent.com/job_monfalcone-pizzeria/artifacts/kcof24bq_Screenshot%202026-03-24%20184600.png",
  seafoodPasta: "https://customer-assets.emergentagent.com/job_monfalcone-pizzeria/artifacts/ajikdo2u_Screenshot%202026-03-24%20155153.png",
  tagliatelle: "https://customer-assets.emergentagent.com/job_monfalcone-pizzeria/artifacts/nfarezl5_Screenshot%202026-03-26%20163531.png",
};

// Animated Section Component
const AnimatedSection = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Star Rating Component
const StarRating = ({ rating, size = 20 }) => {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          size={size}
          className={star <= Math.round(rating) ? "fill-terracotta text-terracotta" : "text-sand"}
        />
      ))}
    </div>
  );
};

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", href: "#home" },
    { name: "Menu", href: "#menu" },
    { name: "Chi Siamo", href: "#chi-siamo" },
    { name: "Galleria", href: "#galleria" },
    { name: "Recensioni", href: "#recensioni" },
    { name: "Contatti", href: "#contatti" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-nav py-3" : "bg-transparent py-5"
      }`}
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <a 
          href="#home" 
          className={`font-heading text-2xl font-semibold tracking-tight transition-colors duration-300 ${
            scrolled ? "text-espresso" : "text-white"
          }`}
          data-testid="nav-logo"
        >
          Al Giardino
        </a>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.name}
              href={link.href}
              className={`text-sm font-medium tracking-wide transition-colors duration-200 ${
                scrolled 
                  ? "text-espresso hover:text-terracotta" 
                  : "text-white/90 hover:text-white"
              }`}
              data-testid={`nav-link-${link.name.toLowerCase().replace(" ", "-")}`}
            >
              {link.name}
            </a>
          ))}
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`md:hidden p-2 ${scrolled ? "text-espresso" : "text-white"}`}
          data-testid="mobile-menu-toggle"
        >
          {isOpen ? <X size={24} /> : <MenuIcon size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="mobile-menu absolute top-full left-0 right-0 py-6 md:hidden"
          data-testid="mobile-menu"
        >
          <div className="flex flex-col gap-4 px-6">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={() => setIsOpen(false)}
                className="text-espresso text-lg font-medium py-2 border-b border-sand"
                data-testid={`mobile-nav-${link.name.toLowerCase().replace(" ", "-")}`}
              >
                {link.name}
              </a>
            ))}
            <a href="tel:0481410669" className="mt-4" data-testid="mobile-phone-button" style={{display: 'none'}}>
              <Button className="btn-primary w-full rounded-full py-3">
                <Phone size={18} className="mr-2" />
                Chiama Ora
              </Button>
            </a>
          </div>
        </motion.div>
      )}
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section
      id="home"
      className="hero-section flex items-center justify-center"
      style={{ backgroundImage: `url(${images.hero})` }}
      data-testid="hero-section"
    >
      <div className="hero-overlay" />
      
      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <p className="text-white/80 uppercase tracking-[0.3em] text-sm mb-4 font-body">
            Pizzeria • Ristorante
          </p>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-semibold text-white mb-6 tracking-tight">
            Al Giardino
          </h1>
          <p className="font-heading text-xl sm:text-2xl text-white/90 italic mb-8">
            {restaurantData.tagline}
          </p>
          
          {/* Rating Badge */}
          <div className="flex items-center justify-center gap-3 mb-10">
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
              <Star size={18} className="fill-terracotta text-terracotta" />
              <span className="text-white font-semibold">{restaurantData.rating}</span>
              <span className="text-white/70">({restaurantData.reviewCount} recensioni)</span>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#menu" data-testid="hero-menu-button">
              <Button className="btn-primary rounded-full px-8 py-6 text-base font-medium">
                <Utensils size={18} className="mr-2" />
                Vedi il Menu
              </Button>
            </a>
          </div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ChevronDown size={32} className="text-white/60" />
        </motion.div>
      </div>
    </section>
  );
};

// Marquee Section
const MarqueeSection = () => {
  const items = [
    "PIZZA COTTA A LEGNA",
    "INGREDIENTI FRESCHI",
    "ATMOSFERA FAMILIARE",
    "SQUISITE PIZZE NAPOLETANE",
    "TRADIZIONE ITALIANA",
    "DAL 1980"
  ];

  return (
    <div className="marquee-wrapper py-4" data-testid="marquee-section">
      <Marquee speed={40} gradient={false}>
        {items.map((item, index) => (
          <span key={index} className="font-heading text-lg tracking-wide mx-8 flex items-center">
            {item}
            <span className="mx-8 opacity-50">•</span>
          </span>
        ))}
      </Marquee>
    </div>
  );
};

// Chi Siamo Section
const ChiSiamoSection = () => {
  return (
    <section id="chi-siamo" className="py-24 lg:py-32 px-6" data-testid="chi-siamo-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Text Content */}
          <AnimatedSection>
            <p className="text-olive uppercase tracking-[0.2em] text-sm font-medium mb-4">
              La Nostra Storia
            </p>
            <h2 className="font-heading text-4xl lg:text-5xl font-semibold text-espresso mb-6 tracking-tight">
              Tradizione e <span className="italic text-terracotta">Passione</span>
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed mb-6">
              Al Giardino è un punto di riferimento per la pizza a Monfalcone. 
              Da generazioni, la nostra famiglia porta avanti la tradizione della vera pizza 
              italiana, cotta nel forno a legna con ingredienti selezionati e tanta passione.
            </p>
            <p className="text-muted-foreground text-lg leading-relaxed mb-8">
              Il nostro locale, con la sua atmosfera familiare e accogliente, è il posto 
              perfetto per una cena tra amici o in famiglia. Venite a trovarci e scoprite 
              perché i nostri clienti continuano a tornare.
            </p>

            {/* Stats */}
            <div className="flex flex-wrap gap-8">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Star size={24} className="fill-terracotta text-terracotta" />
                  <span className="font-heading text-3xl font-semibold text-espresso">
                    {restaurantData.rating}
                  </span>
                </div>
                <p className="text-muted-foreground text-sm">Valutazione media</p>
              </div>
              <div>
                <p className="font-heading text-3xl font-semibold text-espresso mb-1">
                  {restaurantData.reviewCount}+
                </p>
                <p className="text-muted-foreground text-sm">Recensioni</p>
              </div>
              <div>
                <p className="font-heading text-3xl font-semibold text-espresso mb-1">
                  40+
                </p>
                <p className="text-muted-foreground text-sm">Anni di esperienza</p>
              </div>
            </div>
          </AnimatedSection>

          {/* Images */}
          <AnimatedSection delay={0.2} className="relative">
            <div className="relative">
              <div className="photo-frame rounded-lg overflow-hidden">
                <img
                  src={images.interior}
                  alt="Interno del ristorante Al Giardino"
                  className="w-full h-80 object-cover"
                />
              </div>
              <div className="absolute -bottom-12 -left-8 w-48 h-48 photo-frame rounded-lg overflow-hidden hidden lg:block">
                <img
                  src={images.seafoodPasta}
                  alt="Spaghetti ai frutti di mare"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

// Menu Section
const MenuSection = () => {
  const [activeCategory, setActiveCategory] = useState(0);

  return (
    <section id="menu" className="py-24 lg:py-32 px-6 bg-card" data-testid="menu-section">
      <div className="max-w-6xl mx-auto">
        <AnimatedSection className="text-center mb-16">
          <p className="text-olive uppercase tracking-[0.2em] text-sm font-medium mb-4">
            Il Nostro Menu
          </p>
          <h2 className="font-heading text-4xl lg:text-5xl font-semibold text-espresso mb-4 tracking-tight">
            Sapori <span className="italic text-terracotta">Autentici</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Scopri la nostra selezione di pizze, calzoni e specialità di mare. 
            Prezzo medio: {restaurantData.priceRange} a persona.
          </p>
        </AnimatedSection>

        {/* Category Tabs */}
        <AnimatedSection delay={0.1} className="flex flex-wrap justify-center gap-3 mb-12">
          {menuCategories.map((category, index) => (
            <button
              key={category.name}
              onClick={() => setActiveCategory(index)}
              className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-200 ${
                activeCategory === index
                  ? "bg-terracotta text-white"
                  : "bg-sand text-espresso hover:bg-terracotta/10"
              }`}
              data-testid={`menu-tab-${index}`}
            >
              {category.name}
            </button>
          ))}
        </AnimatedSection>

        {/* Menu Items */}
        <AnimatedSection delay={0.2}>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-6">
            {menuCategories[activeCategory].items.map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="py-4 border-b border-sand"
                data-testid={`menu-item-${index}`}
              >
                <div className="flex items-baseline">
                  <h3 className="font-heading text-xl font-medium text-espresso">
                    {item.name}
                  </h3>
                  <div className="menu-item-dots" />
                  <span className="font-heading text-xl font-semibold text-terracotta">
                    €{item.price}
                  </span>
                </div>
                <p className="text-muted-foreground text-sm mt-1">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </AnimatedSection>

        {/* Note */}
        <AnimatedSection delay={0.3} className="text-center mt-12">
          <p className="text-muted-foreground text-sm italic mb-4">
            * Questo non è il menu completo. Per il menu completo chiedi al personale.
          </p>
          <p className="text-muted-foreground text-xs">
            Allergeni: I nostri piatti possono contenere allergeni. Per informazioni dettagliate sugli ingredienti e allergeni presenti, chiedi al nostro personale.
          </p>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Gallery Section
const GallerySection = () => {
  const galleryImages = [
    { src: images.hero, span: "col-span-12 md:col-span-8 row-span-2", alt: "Pizza nel forno a legna" },
    { src: images.exterior, span: "col-span-12 md:col-span-4", alt: "Esterno del ristorante Al Giardino" },
    { src: images.seafoodPasta, span: "col-span-12 md:col-span-4", alt: "Spaghetti ai frutti di mare" },
    { src: images.interior, span: "col-span-12 md:col-span-4", alt: "Interno del ristorante" },
    { src: images.tagliatelle, span: "col-span-12 md:col-span-4", alt: "Tagliatelle al ragù" },
  ];

  return (
    <section id="galleria" className="py-24 lg:py-32 px-6" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto">
        <AnimatedSection className="mb-16">
          <p className="text-olive uppercase tracking-[0.2em] text-sm font-medium mb-4">
            Galleria
          </p>
          <h2 className="font-heading text-4xl lg:text-5xl font-semibold text-espresso tracking-tight">
            Atmosfera <span className="italic text-terracotta">Unica</span>
          </h2>
        </AnimatedSection>

        <div className="bento-grid" data-testid="gallery-grid">
          {galleryImages.map((image, index) => (
            <AnimatedSection
              key={index}
              delay={index * 0.1}
              className={`${image.span} image-hover-zoom rounded-lg overflow-hidden`}
            >
              <img
                src={image.src}
                alt={image.alt}
                className="w-full h-full object-cover min-h-[200px] md:min-h-[250px]"
                data-testid={`gallery-image-${index}`}
              />
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
};

// Reviews Section
const ReviewsSection = () => {
  return (
    <section id="recensioni" className="py-24 lg:py-32 px-6 bg-card" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto">
        <AnimatedSection className="text-center mb-16">
          <p className="text-olive uppercase tracking-[0.2em] text-sm font-medium mb-4">
            Recensioni
          </p>
          <h2 className="font-heading text-4xl lg:text-5xl font-semibold text-espresso mb-4 tracking-tight">
            Cosa Dicono i <span className="italic text-terracotta">Clienti</span>
          </h2>
          <div className="flex items-center justify-center gap-3 mt-6">
            <StarRating rating={restaurantData.rating} size={24} />
            <span className="text-espresso font-semibold text-lg">{restaurantData.rating}</span>
            <span className="text-muted-foreground">({restaurantData.reviewCount} recensioni su Google)</span>
          </div>
        </AnimatedSection>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.map((review, index) => (
            <AnimatedSection key={index} delay={index * 0.1}>
              <div className="review-card p-6 h-full" data-testid={`review-card-${index}`}>
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-olive/20 flex items-center justify-center flex-shrink-0">
                    <span className="font-heading text-xl font-semibold text-olive">
                      {review.author.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <h4 className="font-medium text-espresso">{review.author}</h4>
                    <p className="text-muted-foreground text-xs">{review.badge}</p>
                  </div>
                </div>
                <StarRating rating={review.rating} size={16} />
                <p className="text-muted-foreground mt-4 leading-relaxed text-sm">
                  "{review.text}"
                </p>
                <p className="text-muted-foreground/60 text-xs mt-4">{review.date}</p>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact/Footer Section
const ContactSection = () => {
  return (
    <section id="contatti" className="footer-section py-24 lg:py-32 px-6" data-testid="contact-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <AnimatedSection>
            <p className="text-olive uppercase tracking-[0.2em] text-sm font-medium mb-4">
              Contatti
            </p>
            <h2 className="font-heading text-4xl lg:text-5xl font-semibold text-white mb-8 tracking-tight">
              Vieni a <span className="italic text-terracotta">Trovarci</span>
            </h2>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-terracotta/20 flex items-center justify-center flex-shrink-0">
                  <MapPin size={22} className="text-terracotta" />
                </div>
                <div>
                  <h4 className="font-medium text-white mb-1">Indirizzo</h4>
                  <p className="text-white/70">{restaurantData.address}</p>
                  <a
                    href="https://maps.google.com/?q=Via+I%C2%BA+Maggio+112+Monfalcone"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-terracotta text-sm inline-flex items-center gap-1 mt-2 hover:underline"
                    data-testid="google-maps-link"
                  >
                    Apri in Google Maps <ExternalLink size={14} />
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-terracotta/20 flex items-center justify-center flex-shrink-0">
                  <Phone size={22} className="text-terracotta" />
                </div>
                <div>
                  <h4 className="font-medium text-white mb-1">Telefono</h4>
                  <a
                    href="tel:0481410669"
                    className="text-white/70 hover:text-terracotta transition-colors"
                    data-testid="phone-link"
                  >
                    {restaurantData.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-terracotta/20 flex items-center justify-center flex-shrink-0">
                  <Clock size={22} className="text-terracotta" />
                </div>
                <div>
                  <h4 className="font-medium text-white mb-1">Orari</h4>
                  <p className="text-white/70">{restaurantData.hours}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-terracotta/20 flex items-center justify-center flex-shrink-0">
                  <Facebook size={22} className="text-terracotta" />
                </div>
                <div>
                  <h4 className="font-medium text-white mb-1">Social</h4>
                  <a
                    href={`https://${restaurantData.facebook}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/70 hover:text-terracotta transition-colors inline-flex items-center gap-1"
                    data-testid="facebook-link"
                  >
                    Seguici su Facebook <ExternalLink size={14} />
                  </a>
                </div>
              </div>
            </div>

            {/* Services */}
            <div className="mt-10 pt-8 border-t border-white/10">
              <h4 className="font-medium text-white mb-4">Servizi Disponibili</h4>
              <div className="flex flex-wrap gap-3">
                {restaurantData.services.map((service, index) => (
                  <span
                    key={index}
                    className="px-4 py-2 bg-white/5 rounded-full text-white/80 text-sm"
                  >
                    {service}
                  </span>
                ))}
              </div>
            </div>

            {/* Parking */}
            <div className="mt-8 pt-8 border-t border-white/10">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-terracotta/20 flex items-center justify-center flex-shrink-0">
                  <MapPin size={22} className="text-terracotta" />
                </div>
                <div>
                  <h4 className="font-medium text-white mb-2">Parcheggio</h4>
                  <p className="text-white/70 text-sm">
                    Ampio parcheggio gratuito disponibile direttamente davanti al ristorante. 
                    Comodo accesso dalla statale.
                  </p>
                </div>
              </div>
            </div>
          </AnimatedSection>

          {/* Map Embed */}
          <AnimatedSection delay={0.2}>
            <div className="h-full min-h-[400px] rounded-lg overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2770.1234567890123!2d13.530943!3d45.806691!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x477b6e7a4b5c3d2e%3A0x1234567890abcdef!2sVia%20I%C2%BA%20Maggio%2C%20112%2C%2034074%20Monfalcone%20GO!5e0!3m2!1sit!2sit!4v1710000000000"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: "400px" }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Mappa Al Giardino"
                className="rounded-lg"
                data-testid="google-map-embed"
              />
            </div>
          </AnimatedSection>
        </div>

        {/* Footer Bottom */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <Flame size={20} className="text-terracotta" />
            <span className="font-heading text-xl font-semibold text-white">Al Giardino</span>
          </div>
          <p className="text-white/50 text-sm text-center">
            © {new Date().getFullYear()} Pizzeria Al Giardino. Tutti i diritti riservati.
          </p>
        </div>
      </div>
    </section>
  );
};

// Main HomePage Component
export default function HomePage() {
  return (
    <main data-testid="home-page">
      <Navigation />
      <HeroSection />
      <MarqueeSection />
      <ChiSiamoSection />
      <MenuSection />
      <GallerySection />
      <ReviewsSection />
      <ContactSection />
    </main>
  );
}
